document.documentElement.classList.remove('no-js');

/* ---------------------------------------------------------------------------
   Mobile menu
   ------------------------------------------------------------------------- */
(function initMenu() {
  const toggles = document.querySelectorAll('[data-menu-toggle]');
  const menu = document.querySelector('[data-mobile-menu]');
  if (!menu) return;

  function closeMenu() {
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
  }

  function openMenu() {
    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    toggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
  }

  toggles.forEach((toggle) => {
    toggle.addEventListener('click', () => {
      menu.classList.contains('is-open') ? closeMenu() : openMenu();
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeMenu();
  });

  menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
})();

/* ---------------------------------------------------------------------------
   Scroll-in animation
   ------------------------------------------------------------------------- */
(function initScrollAnimation() {
  const targets = document.querySelectorAll('[data-animate]');
  if (!targets.length) return;

  if (!('IntersectionObserver' in window)) {
    targets.forEach((t) => t.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
  );

  targets.forEach((t) => observer.observe(t));
})();

/* ---------------------------------------------------------------------------
   Intro banner: sticky, scroll-linked line-by-line text reveal
   ------------------------------------------------------------------------- */
(function initIntroReveal() {
  const wraps = document.querySelectorAll('[data-intro-reveal]');
  if (!wraps.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  const groups = Array.from(wraps)
    .map((wrap) => ({
      wrap,
      lines: Array.from(wrap.querySelectorAll('.intro-banner__line')),
    }))
    .filter((group) => group.lines.length);
  if (!groups.length) return;

  function update() {
    const viewportHeight = window.innerHeight;
    groups.forEach(({ wrap, lines }) => {
      const rect = wrap.getBoundingClientRect();
      const scrollableDistance = rect.height - viewportHeight;
      let progress;
      if (scrollableDistance > 0) {
        progress = -rect.top / scrollableDistance;
      } else {
        progress = rect.top <= 0 ? 1 : 0;
      }
      progress = Math.min(Math.max(progress, 0), 1);

      const revealCount = Math.ceil(progress * lines.length);
      lines.forEach((line, i) => {
        line.classList.toggle('is-visible', i < revealCount);
      });
    });
  }

  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(() => {
      update();
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  update();
})();

/* ---------------------------------------------------------------------------
   Carousels (Become a Supplier / featured products)
   ------------------------------------------------------------------------- */
document.querySelectorAll('[data-carousel]').forEach((carousel) => {
  const track = carousel.querySelector('[data-carousel-track]');
  const prev = carousel.querySelector('[data-carousel-prev]');
  const next = carousel.querySelector('[data-carousel-next]');
  if (!track) return;

  function scrollByAmount(direction) {
    const slide = track.querySelector('[data-carousel-slide]');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const amount = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    track.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  prev && prev.addEventListener('click', () => scrollByAmount(-1));
  next && next.addEventListener('click', () => scrollByAmount(1));

  if (carousel.hasAttribute('data-carousel-autoplay')) {
    const delay = parseInt(carousel.getAttribute('data-carousel-autoplay'), 10) || 4000;
    let timer = null;

    function advance() {
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      if (atEnd) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        scrollByAmount(1);
      }
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      if (timer || track.scrollWidth <= track.clientWidth) return;
      timer = setInterval(advance, delay);
    }

    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', start);
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', start);
    carousel.addEventListener('touchstart', stop, { passive: true });

    start();
    window.addEventListener('resize', () => {
      stop();
      start();
    });
  }
});

/* ---------------------------------------------------------------------------
   Cart drawer + AJAX add to cart
   ------------------------------------------------------------------------- */
(function initCart() {
  const drawer = document.getElementById('CartDrawer');
  const body = document.getElementById('CartDrawerBody');
  if (!drawer) return;

  function openDrawer() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.querySelectorAll('[data-cart-open]').forEach((el) =>
    el.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer();
      refreshCart();
    })
  );

  drawer.querySelectorAll('[data-cart-close]').forEach((el) => el.addEventListener('click', closeDrawer));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
  });

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.dataset.empty = count === 0 ? 'true' : 'false';
    });
  }

  function renderCart(cart) {
    if (!body) return;
    if (!cart.items.length) {
      body.innerHTML = '<p class="cart-empty">Your cart is empty.</p>';
      return;
    }
    body.innerHTML = cart.items
      .map(
        (item) => `
        <div class="cart-line-item">
          <img class="cart-line-item__image" src="${item.image || ''}" alt="${item.product_title}" loading="lazy">
          <div>
            <p class="cart-line-item__title">${item.product_title}</p>
            <p class="cart-line-item__meta">${item.variant_title || ''} &middot; Qty ${item.quantity}</p>
            <p class="cart-line-item__meta">${(item.final_line_price / 100).toFixed(2)}</p>
          </div>
        </div>`
      )
      .join('');
  }

  function refreshCart() {
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        updateCartCount(cart.item_count);
        renderCart(cart);
      })
      .catch(() => {});
  }

  document.querySelectorAll('[data-add-to-cart]').forEach((button) => {
    button.addEventListener('click', () => {
      const variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;
      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Adding...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      })
        .then((r) => r.json())
        .then(() => {
          refreshCart();
          openDrawer();
        })
        .catch(() => {})
        .finally(() => {
          button.disabled = false;
          button.textContent = originalText;
        });
    });
  });

  refreshCart();
})();
